<template>
    <div class="favorite-list-container">
        <!-- 載入狀態 -->
        <div v-if="isLoading" class="loading-section">
            <div class="loading-spinner"></div>
            <span>{{ $t('aiprompt.loadingFavorites') }}</span>
        </div>

        <!-- 收藏列表 -->
        <div v-else-if="favorites.length > 0" class="favorites-grid">
            <div
                v-for="favorite in favorites"
                :key="favorite.id"
                class="favorite-card"
                @click="openFavoriteDetail(favorite)"
            >
                <div class="card-content">
                    <h3 class="collection-name">{{ favorite.CollectionName || favorite.collectionName  }}</h3>
                    <div class="creator-info">
                        <span class="creator-label">{{ $t('aiprompt.creator') }}：</span>
                        <span class="creator-name">{{ favorite.CreatorName || favorite.creatorName  }}</span>
                    </div>
                </div>
                <button
                    class="star-btn"
                    @click.stop="removeFavorite(favorite)"
                    :title="$t('aiprompt.removeFavorite')"
                >
                    <svg-icon icon-class="star-fill" class="star-icon" />
                </button>
            </div>
        </div>

        <!-- 空狀態 -->
        <div v-else class="empty-section">
            <div class="empty-icon">
                <svg-icon icon-class="star" />
            </div>
            <p>{{ $t('aiprompt.noFavoritesYet') }}</p>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import { Message } from 'view-ui-plus'
import aipromptAPI from '@/api/aipromptAPI'
import jwt_decode from 'jwt-decode'
import SvgIcon from '@/components/SvgIcon.vue'

const { t } = useI18n()

// 定義 emit 事件
const emit = defineEmits(['select-favorite'])

// 狀態管理
const favorites = ref([])
const isLoading = ref(false)

// 用戶資訊
const userId = ref('')

// 獲取用戶 ID
const getUserId = () => {
    const idToken = localStorage.getItem('id_token')
    if (idToken) {
        try {
            const decoded = jwt_decode(idToken)
            userId.value = decoded.sub
        } catch (error) {
            console.error('解析 token 失敗:', error)
        }
    }
}

// 載入收藏列表
const loadFavorites = async () => {
    if (!userId.value) {
        return
    }

    isLoading.value = true

    try {
        const response = await aipromptAPI.getUserFavorites({
            userId: userId.value
        })

        if (response && response.success) {
            favorites.value = response.favorites
            // console.log('載入收藏列表成功:', favorites.value)
            // console.log('第一個收藏項目:', favorites.value[0])
            // console.log('CollectionName:', favorites.value[0]?.CollectionName)
            // console.log('CreatorName:', favorites.value[0]?.CreatorName);
        } else {
            favorites.value = []
        }
    } catch (error) {
        console.error('載入收藏列表失敗:', error)
        Message.error(t('aiprompt.messages.loadFavoritesFailed'))
        favorites.value = []
    } finally {
        isLoading.value = false
    }
}

// 打開收藏詳情
const openFavoriteDetail = (favorite) => {
    // 發送事件給父組件
    emit('select-favorite', favorite)
}

// 移除收藏
const removeFavorite = async (favorite) => {
    try {
        const response = await aipromptAPI.removeFavorite({
            favoriteId: favorite.id
        })

        if (response && response.success) {
            Message.success(t('aiprompt.messages.unfavorited'))
            // 重新載入收藏列表
            await loadFavorites()
        }
    } catch (error) {
        console.error('移除收藏失敗:', error)
        Message.error(t('aiprompt.messages.unfavoriteFailed'))
    }
}

// 生命週期
onMounted(async () => {
    getUserId()
    await loadFavorites()
})
</script>

<style lang="less" scoped>
@import "../assets/HTwbCardCSS/HTwbCardVal.less";

.favorite-list-container {
    width: 100%;
    padding: 1rem 0;
}

// 載入狀態
.loading-section {
    text-align: center;
    padding: 4rem 2rem;

    .loading-spinner {
        width: 40px;
        height: 40px;
        border: 4px solid #f3f3f3;
        border-top: 4px solid @primary-color;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto 1rem;
    }

    span {
        color: @text-color-gray;
        font-size: 1rem;
    }
}

// 收藏卡片網格
.favorites-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;

    @media (max-width: 1200px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
}

.favorite-card {
    background: #f5f5f5;
    border: 2px solid #d0d0d0;
    border-radius: 8px;
    padding: 1.5rem;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    min-height: 100px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &:hover {
        border-color: #a0a0a0;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .card-content {
        flex: 1;
        padding-right: 3rem;

        .collection-name {
            color: @text-color;
            font-size: 1.2rem;
            font-weight: 600;
            margin: 0 0 0.8rem 0;
            line-height: 1.4;
        }

        .creator-info {
            color: @text-color-gray;
            font-size: 14px;
            margin: 0 0 0.8rem 0;

            .creator-label {
                font-weight: 400;
            }

            .creator-name {
                color: gray;
                font-weight:bolder;
            }
        }

        .divider {
            border: none;
            border-top: 1px solid #d0d0d0;
            margin: 0;
        }
    }

    .star-btn {
        position: absolute;
        bottom: 1rem;
        right: 1rem;
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 0.25rem;
        transition: transform 0.2s ease;
        z-index: 10;

        .star-icon {
            width: 32px;
            height: 32px;
            color: #FF9800;
        }

       
    }
}

// 空狀態
.empty-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    color: @text-color-gray;

    .empty-icon {
        font-size: 4rem;
        margin-bottom: 1rem;
        opacity: 0.5;

        .svg-icon {
            width: 4rem;
            height: 4rem;
            color: @text-color-gray;
            fill: currentColor;
        }
    }

    p {
        font-size: 1.1rem;
        margin: 0;
    }
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
</style>
